// src/pages/Boutique.jsx
import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import ProductCard from '../components/product/ProductCard';

const heroImg = 'https://madadline.com/images/a1/image.jpg';

const FAQ_KEYS = ['size', 'delay', 'exchange'];

function Boutique() {
  const { t } = useTranslation();

  const [products, setProducts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  const [activeSort, setActiveSort]     = useState('new');
  const [visibleCount, setVisibleCount] = useState(8);
  const [openFaq, setOpenFaq]           = useState(null);
  const [filterMobileOpen, setFilterMobileOpen] = useState(false);

  const sortOptions = useMemo(() => [
    { key: 'new',  label: t('shop.sortNew') },
    { key: 'asc',  label: t('shop.sortAsc') },
    { key: 'desc', label: t('shop.sortDesc') },
  ], [t]);

  useEffect(() => {
    setLoading(true);
    axios
      .get('https://madadline.com/api/products')
      .then(res => {
        const data = res.data;
        let list = [];
        if (Array.isArray(data)) list = data;
        else if (Array.isArray(data.data)) list = data.data;
        else if (Array.isArray(data.products)) list = data.products;
        setProducts(list);
        setError(null);
      })
      .catch(err => {
        console.error('Erreur boutique:', err);
        setError(t('shop.errorLoading'));
        setProducts([]);
      })
      .finally(() => setLoading(false));
  }, [t]);

  const sorted = useMemo(() => {
    const list = [...products];
    if (activeSort === 'asc') {
      list.sort((a, b) => Number(a.prix) - Number(b.prix));
    } else if (activeSort === 'desc') {
      list.sort((a, b) => Number(b.prix) - Number(a.prix));
    } else {
      list.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    }
    return list;
  }, [products, activeSort]);

  return (
    <main className="shop">

      <section className="shop__hero">
        <div className="shop__hero-bg">
          <img src={heroImg} alt={t('shop.heroTitleLine1') + ' ' + t('shop.heroTitleEm')} loading="eager" />
        </div>
        <div className="shop__hero-overlay" />
        <div className="shop__hero-inner">
          <span className="shop__hero-label">{t('shop.heroLabel')}</span>
          <h1 className="shop__hero-title">
            {t('shop.heroTitleLine1')} <em>{t('shop.heroTitleEm')}</em>
          </h1>
          <p className="shop__hero-text">{t('shop.heroText')}</p>
        </div>
      </section>

      <section className="shop__toolbar">
        <div className="shop__toolbar-inner">
          <button
            className="shop__toolbar-filter"
            onClick={() => setFilterMobileOpen(!filterMobileOpen)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <line x1="4" y1="6" x2="20" y2="6" />
              <line x1="4" y1="12" x2="14" y2="12" />
              <line x1="4" y1="18" x2="10" y2="18" />
            </svg>
            {t('shop.filter')}
          </button>

          <span className="shop__toolbar-count">
            {sorted.length} {sorted.length > 1 ? t('shop.pieces') : t('shop.piece')}
          </span>

          <div className="shop__toolbar-sort">
            <label htmlFor="sort">{t('shop.sortBy')}</label>
            <select
              id="sort"
              value={activeSort}
              onChange={e => setActiveSort(e.target.value)}
            >
              {sortOptions.map(s => (
                <option key={s.key} value={s.key}>{s.label}</option>
              ))}
            </select>
          </div>
        </div>
      </section>

      <section className="shop__content">
        {loading && (
          <div className="shop__loading"><div className="spinner" /></div>
        )}

        {!loading && error && (
          <div className="shop__empty">
            <p>{error}</p>
            <button onClick={() => window.location.reload()} className="shop__btn-primary">
              {t('shop.retry')}
            </button>
          </div>
        )}

        {!loading && !error && sorted.length === 0 && (
          <div className="shop__empty">
            <p>{t('shop.emptyTitle')}</p>
            <Link to="/contact" className="shop__btn-primary">
              {t('shop.contactUs')}
            </Link>
          </div>
        )}

        {!loading && !error && sorted.length > 0 && (
          <>
            <div className="shop__grid">
              {sorted.slice(0, visibleCount).map(p => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>

            {visibleCount < sorted.length && (
              <div className="shop__more">
                <button
                  className="shop__more-btn"
                  onClick={() => setVisibleCount(v => v + 8)}
                >
                  {t('shop.seeMore')}
                </button>
                <span className="shop__more-progress">
                  {Math.min(visibleCount, sorted.length)} / {sorted.length}
                </span>
              </div>
            )}
          </>
        )}
      </section>

      <section className="shop__faq">
        <div className="shop__faq-inner">
          <header className="shop__faq-head">
            <span className="shop__faq-label">{t('shop.faqLabel')}</span>
            <h2 className="shop__faq-title">{t('shop.faqTitle')}</h2>
          </header>

          <div className="shop__faq-list">
            {FAQ_KEYS.map((key, i) => (
              <div
                key={key}
                className={`shop__faq-item ${openFaq === i ? 'is-open' : ''}`}
              >
                <button
                  className="shop__faq-question"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span>{t(`shop.faqItems.${key}.q`)}</span>
                  <span className="shop__faq-icon">{openFaq === i ? '−' : '+'}</span>
                </button>
                {openFaq === i && (
                  <div className="shop__faq-answer">{t(`shop.faqItems.${key}.a`)}</div>
                )}
              </div>
            ))}
          </div>

          <div className="shop__faq-cta">
            <a
              href="https://wa.me/212669428878"
              target="_blank"
              rel="noreferrer"
              className="shop__faq-wa"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
              </svg>
              {t('shop.faqAnother')}
            </a>
          </div>
        </div>
      </section>

    </main>
  );
}

export default Boutique;